<template>
  <a-row :gutter="[8, 8]" class="h-100">
    <a-col span="10" class="d-flex flex-column">
      <!-- Video -->
      <a-card
        size="small"
        hoverable
        class="d-flex flex-column"
        style="height: 50%"
        :body-style="{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          padding: '8px',
          height: '100%',
        }"
      >
        <a-spin v-if="isFetchingVideoUrl" size="large" class="m-auto" />

        <!-- Video -->
        <div
          v-else-if="video?.isVideoExists"
          style="position: relative"
          class="w-100 h-100"
        >
          <div
            v-if="!showVideoControls"
            style="
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              background-color: rgba(0, 0, 0, 0.3);
              z-index: 1;
              display: flex;
              justify-content: center;
              align-items: center;
            "
          >
            <a-spin size="large" />
          </div>

          <a-dropdown :trigger="['click']" placement="bottomRight">
            <MoreOutlined class="video-options background-animation" />
            <template #overlay>
              <a-menu>
                <a-popconfirm
                  :title="`Are you sure you want to mark this as a ${
                    !traceCycleData?.is_good_cycle ? 'Good' : 'Bad'
                  } cycle?`"
                  ok-text="Yes"
                  cancel-text="No"
                  @confirm="toggleCycleStatus()"
                >
                  <a-menu-item>
                    <a-space>
                      <ToolOutlined />
                      <span>
                        {{ `Mark as ${cycleType}` }}
                      </span>
                    </a-space>
                  </a-menu-item>
                </a-popconfirm>
                <!-- <a-menu-item v-if="role == roles.support_user"> -->

                <a-popconfirm
                  title="Are you sure you want to archive this cycle?"
                  ok-text="Yes"
                  cancel-text="No"
                  @confirm="archiveCycle(video)"
                >
                  <a-menu-item v-if="role == roles.support_user">
                    <a-space>
                      <DeleteOutlined />
                      <span> Archive cycle </span>
                    </a-space>
                  </a-menu-item>
                </a-popconfirm>

                <a-menu-item
                  v-if="isSupportUser"
                  :disabled="isDownloading"
                  @click="downloadVideoWithDetections"
                >
                  <a-space>
                    <LoadingOutlined v-if="isDownloading" />
                    <DownloadOutlined v-else />
                    <span> Download video + detections </span>
                  </a-space>
                </a-menu-item>
              </a-menu>
            </template>
          </a-dropdown>
          <video
            id="trace-page-single-video-play"
            ref="video"
            class="w-100 rounded h-100"
            controls
            :src="video?.fileURL"
            :controlslist="isVisualization ? 'nofullscreen' : ''"
            disablepictureinpicture
            @play="onPlay"
            @pause="onPause"
            @seeking="handleSeeking"
            @ended="onEnd"
            @loadeddata="onLoad"
          />
          <BoundingBoxes
            v-if="showBoundingBoxes"
            ref="boundingBoxes"
            :video-player="$refs.video"
            :current-frame="frame"
            :predictions="filePreds"
          />
        </div>

        <!-- Untraced Cycle -->
        <div v-else class="w-100 d-flex" style="height: 40vh !important">
          <a-result title="Untraced Cycle!" class="m-auto">
            <template #icon>
              <exclamation-circle-outlined class="text-danger" />
            </template>
          </a-result>
        </div>
      </a-card>

      <!-- Timestamp Tags -->
      <timestamp-tags
        v-if="video?.isVideoExists"
        :task-record-id="video.id"
        :current-video-time="currentVideoTime"
        :substepList="substepList"
        @skipTo="skipTo"
      />
    </a-col>

    <a-col span="8">
      <!-- Steps List -->
      <steps-list
        :steps-list="stepsList"
        :negative-steps-list="negativeSteps"
        :optional-steps-list="optionalSteps"
        :low-confidence-steps-list="visibleNonCriticalStepIndices"
        :missed-steps-list="missedSteps"
        :background-time="backgroundTime"
        :current-step-list="currentStepList"
        :cycle="currentCycle"
        :detected-steps="detectedSteps"
        :steps-time="stepsTime"
        :substep-metrics="substepMetrics"
        :is-task-details-loading="isFetchingTaskDetails"
        :is-prediction-file-not-exist="isPredictionFileNotExist"
        :steps-translation-mapping="stepsTranslationMapping"
        :process-details="processDetails"
        :cycle-time="cycleTime + backgroundTime"
        :propagated-steps="propagatedSteps"
        @handleClickSubStep="handleClickSubStep"
      />
    </a-col>

    <a-col span="6" class="d-flex flex-column">
      <!-- Potential Issues (clickable, seeks video to when the step was missed) -->
      <potential-issues
        :items="potentialIssues"
        :title="'Potential Issues'"
        :steps-translation-mapping="stepsTranslationMapping"
        :is-task-details-loading="isFetchingTaskDetails"
        @click-step="handleClickMissedStep"
      />

      <a-card
        v-if="video?.isVideoExists"
        size="small"
        hoverable
        class="h-50 d-flex flex-column mt-2"
        style="flex: 60%"
        :body-style="{
          height: '1px',
          flex: '1 1 auto',
          overflowY: 'auto',
          background: '#f8f9fa',
        }"
        :tab-list="tabsList"
        :active-tab-key="currentTab"
        @tabChange="(key) => (currentTab = key)"
      >
        <!-- Cycle Meta -->
        <cycle-meta
          v-if="isMetaExist && currentTab === 'meta'"
          :json-data="traceCycleData?.metadata"
        />
        <!-- <a-descriptions v-if="isMetaExist" :column="1" bordered>
            <a-descriptions-item
              v-for="(value, key) in traceCycleData?.metadata"
              :key="key"
              :label="key"
              :label-style="{
                textTransform: 'capitalize',
              }"
            >
              {{ value }}
            </a-descriptions-item>
          </a-descriptions>
          <a-list /> -->

        <!-- Comments -->
        <comments
          v-else-if="currentTab === 'comments'"
          :comments="comments"
          :selected-video="video"
          :is-minimized="true"
          :is-fetching-video-url="isFetchingVideoUrl"
          @updateVideoListCount="updateVideoListCount"
          @updateVideo="(args) => $emit('updateVideo', args)"
        />

        <!-- Missed Steps (original list, as before) -->
        <missed-steps
          v-else-if="currentTab === 'missedSteps'"
          :video="video"
          :missed-steps="!isGoodCycle ? missedSteps : []"
          :title="'Missed Steps'"
          :steps-translation-mapping="stepsTranslationMapping"
          :is-task-details-loading="isFetchingTaskDetails"
        />
      </a-card>
    </a-col>
  </a-row>
</template>

<script>
import {
  DeleteOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  MoreOutlined,
  ToolOutlined,
} from '@ant-design/icons-vue';
import axios from 'axios';
import { saveAs } from 'file-saver';
import MissedSteps from 'src/components/user-panel/pages/DeployModel/components/MissedStep';
import PotentialIssues from 'src/components/user-panel/pages/DeployModel/components/PotentialIssues';
import StepsList from 'src/components/user-panel/pages/DeployModel/components/StepsList';
import BoundingBoxes from 'src/components/user-panel/pages/trace/BoundingBoxes.vue';
import Comments from 'src/components/user-panel/pages/trace/Comments.vue';
import CycleMeta from 'src/components/user-panel/pages/trace/CycleMeta.vue';
import { roles } from 'src/config/roles-config';
import S3Service from 'src/services/s3';
import TelemetryService from 'src/services/telemetry';
import { mapActions, mapGetters } from 'vuex';
import TimestampTags from './TimestampTags.vue';
export default {
  name: 'RecordedInference',
  components: {
    ToolOutlined,
    DeleteOutlined,
    DownloadOutlined,
    LoadingOutlined,
    MoreOutlined,
    MissedSteps,
    PotentialIssues,
    StepsList,
    Comments,
    ExclamationCircleOutlined,
    TimestampTags,
    BoundingBoxes,
    CycleMeta,
  },
  inject: ['toast'],
  props: {
    navigate: { type: Boolean, default: false },
    isFetchingVideoUrl: { type: Boolean, default: false },
    video: { type: Object, default: () => ({}) },
    stepsList: { type: Array, default: () => [] },
    comments: { type: Array, default: () => [] },
    isVisualization: { type: Boolean, default: false },
    showBoundingBoxes: { type: Boolean, default: false },
  },
  emits: [
    'loading',
    'updateVideoListCount',
    'updateVideo',
    'toggleCycleStatus',
    'archivedCycleCount',
    'showVisualization',
    'showClientVisualization',
  ],

  data() {
    return {
      roles,
      isGoodCycle: false,
      frame: null,
      currentStepList: [],
      currentCycle: 1,
      filePreds: null,
      stepsStartPoints: {},
      missedStepsStartPoints: {},
      stepsTime: {},
      substepList: [],
      backgroundTime: 0,
      missedSteps: [],
      detectedSteps: {},
      isPredictionFileNotExist: false,
      processDetails: {},
      showVideoControls: false,
      isDownloading: false,
      propagatedSteps: [],
      stepsLegends: [
        { label: 'Completed Steps', background: 'list-group-item-success' },
        { label: 'Current Step', background: 'list-group-item-warning' },
        { label: 'Missed Steps', background: 'list-group-item-danger' },
        { label: 'Propagated Steps', background: 'list-group-item-secondary' },
      ],
      traceCycleData: null,
      currentVideoTime: 0,
      currentTab: 'meta',
      tabsList: [
        {
          key: 'meta',
          tab: 'Meta',
        },
        {
          key: 'comments',
          tab: 'Comments',
        },
        {
          key: 'missedSteps',
          tab: 'Missed Steps',
        },
      ],
      missedStepsFromTelemetry: [],
      cycleMeta: {},
      cycleTime: 0,
      taskId: null,
      historyId: null,
      videoRef: null,
      animationFrameId: null,
      substepMetrics: {}, // Track VA, NVA, and Total time for each substep
      precomputedMetrics: {}, // Lookup table: frame -> metrics snapshot
    };
  },

  computed: {
    ...mapGetters([
      'negativeSteps',
      'optionalSteps',
      'taskStepsCount',
      'taskJson',
      'isFetchingTaskDetails',
      'stepsTranslationMapping',
      'role',
      'organization',
      'visibleNonCriticalStepIndices',
    ]),

    isSupportUser() {
      return this.role === roles.support_user;
    },

    cycleType() {
      return !this.traceCycleData?.is_good_cycle ? 'Good' : 'Bad';
    },

    isMetaExist() {
      return (
        this.traceCycleData?.metadata &&
        Object.keys(this.traceCycleData?.metadata).length
      );
    },

    potentialIssues() {
      if (this.isGoodCycle) return [];
      const fps = this.video?.fps || 30;
      return (this.missedSteps || []).map((name) => {
        const idx = this.substepList.find((s) => s.name === name)?.substepIndex;
        const frame =
          idx != null ? this.missedStepsStartPoints[idx] : undefined;
        return {
          name,
          time: frame !== undefined ? Number(frame) / fps : null,
        };
      });
    },
  },

  watch: {
    video: {
      handler(newValue, oldValue) {
        if (newValue?.cycle_id !== oldValue?.cycle_id) {
          this.handleFetchRequiredData();

          const steps = this.video.missed_steps?.split(', ');
          if (!steps.length) return;
          this.missedStepsFromTelemetry = steps.map((s) =>
            s ? Number(s) : ''
          );
        }
        this.updateMissedSteps();
      },
      deep: true,
    },

    frame(currentFrame) {
      this.currentStepList =
        this.filePreds[currentFrame]?.['pred']?.filter(
          (el) => !this.missedStepsFromTelemetry?.includes(el)
        ) || [];
      this.updateSubstepTimes();
      this.substepMetrics = this.precomputedMetrics[currentFrame] || {};
    },

    stepsList(list) {
      this.substepList = list.filter((el) => el.type === 'sub-step');
    },

    currentStepList(list) {
      list.forEach(this.addStepInDetectedStepsDict);
    },

    currentCycle() {
      this.detectedSteps = {};
      this.stepsTime = {};
      this.substepMetrics = {};
      this.precomputedMetrics = {};
    },
  },

  mounted() {
    const tempStepsList = this.stepsList.filter((el) => el.type === 'sub-step');
    this.substepList = tempStepsList;
    this.setStepsStartTime(tempStepsList);
    this.handleFetchRequiredData();
    this.videoRef = this.$refs.video;
  },

  beforeUnmount() {
    this.resetState();
  },

  methods: {
    ...mapActions(['fetchTaskDetails']),
    updateVideoListCount(videoIndex, commentLength) {
      this.$emit('updateVideoListCount', videoIndex, commentLength);
    },
    setStepsStartTime(stepsList = this.substepList) {
      this.stepsTime = stepsList.reduce((res, el) => {
        res[el.substepIndex] = 0;
        return res;
      }, {});
    },

    async toggleCycleStatus() {
      let isGoodCycle = !this.traceCycleData.is_good_cycle;
      this.traceCycleData.is_good_cycle = isGoodCycle;
      this.isGoodCycle = isGoodCycle;
      this.updateMissedSteps();
      this.$emit('toggleCycleStatus', { video: this.video, isGoodCycle });
    },

    // Downloads the cycle video and its detections json as two separate files.
    async downloadVideoWithDetections() {
      if (!this.isSupportUser) return;
      const { fileURL, fileName, device_id } = this.video || {};
      if (!fileURL) {
        this.toast.error('Video URL is not available.');
        return;
      }

      // S3 serves these as binary/octet-stream, so navigating to the URL
      // hands the video to the browser's download manager straight away
      // (with its native file name) instead of buffering it in memory.
      this.triggerBrowserDownload(fileURL);

      this.isDownloading = true;
      try {
        await this.downloadDetections(fileName, device_id);
      } catch (error) {
        console.error('[download] detections failed', error?.message);
        this.toast.info('Detections file not found for this video.');
      } finally {
        this.isDownloading = false;
      }
    },

    // Detections live beside Processed/ but with '-' instead of ':' in the
    // timestamp: <device_id>/<task_id>/Detections/<fileName>_detections.json
    async downloadDetections(fileName, deviceId) {
      const taskId = this.taskId || this.$route.query.taskId;
      if (!fileName || !deviceId || !taskId) {
        throw new Error('Missing detections path details');
      }
      const detectionsFile = `${fileName.replace(/:/g, '-')}_detections.json`;
      const [error, data] = await S3Service.generatePresignedUrl(
        `${this.organization}-videos`,
        `${deviceId}/${taskId}/Detections/${detectionsFile}`,
        '',
        false
      );
      if (error || !data?.presigned_url) {
        throw new Error('Could not get detections url');
      }
      const { data: blob } = await axios.get(data.presigned_url, {
        responseType: 'blob',
      });
      saveAs(blob, detectionsFile);
    },

    triggerBrowserDownload(url) {
      const link = document.createElement('a');
      link.href = url;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
    },

    async archiveCycle(video) {
      await TelemetryService.archiveCycle(video.cycle_id);
      this.emitter.emit('getAnalyticsAndTrace', true);
      this.$emit('archivedCycleCount', video.is_good_cycle);
      if (this.navigate) {
        this.$router.push('/');
      }
    },

    updateSubstepTimes(frame = this.frame) {
      const stepsObj = this?.filePreds[frame];
      if (!stepsObj) return;
      stepsObj.pred = stepsObj?.pred.filter(
        (el) => !this.missedStepsFromTelemetry?.includes(el)
      );

      const tempStepsTime = { ...this.stepsTime };

      const { pred, times } = stepsObj;
      if (pred[0] === this.taskStepsCount) {
        this.backgroundTime = times[0] || 0;
        return;
      }

      this.cycleTime += times[0] - (this.stepsTime[pred[0]] || 0);
      pred.forEach((step, index) => {
        tempStepsTime[step] = times[index];
      });
      this.stepsTime = tempStepsTime;
    },

    async onLoad() {
      this.showVideoControls = true;
      this.$refs.boundingBoxes?.setupCanvas();
    },

    onPlay() {
      const renderLoop = () => {
        this.highlightCurrentStep();
        this.$refs.boundingBoxes?.drawBoundingBoxes();
        this.animationFrameId = requestAnimationFrame(renderLoop);
      };
      this.animationFrameId = requestAnimationFrame(renderLoop);
    },

    onPause() {
      cancelAnimationFrame(this.animationFrameId);
    },

    onEnd() {
      cancelAnimationFrame(this.animationFrameId);
    },

    addStepInDetectedStepsDict(step) {
      const temp = { ...this.detectedSteps };
      if (this.missedStepsFromTelemetry?.includes(step)) temp[step] = false;
      else temp[step] = true;
      this.detectedSteps = temp;
    },

    highlightCurrentStep() {
      try {
        const currentTime = this.$refs?.video?.currentTime.toFixed(5);
        this.currentVideoTime = currentTime;
        const num = currentTime * (this.video?.fps || 30);

        this.frame = Math.floor(num);
      } catch (error) {
        console.log('update frame error', error);
        this.resetState();
      }
    },

    async handleFetchRequiredData() {
      const { traceId } = this.$route.params;
      const { cycle_id } = this.$route.query;
      let query;
      if (traceId && cycle_id) {
        query = { cycle_id, task_record_id: traceId };
      } else {
        query = {
          cycle_id: this.video?.cycle_id,
          task_record_id: this.video?.id,
        };
      }

      if (query.cycle_id && query.task_record_id) {
        await this.handleGetTraceRecordData(query);
        await this.fetchTaskDetails({
          taskId: Number(this.taskId),
          historyId: this.historyId,
        });
      }

      this.updatePredictions();
      this.updateMissedSteps();
    },

    async handleGetTraceRecordData(query) {
      if (this.video?.cycleData) {
        this.traceCycleData = this.video.cycleData;
        this.isGoodCycle = this.video.cycleData?.is_good_cycle;
        if (this.traceCycleData.history_id) {
        this.historyId = this.traceCycleData.history_id
      }
      if (this.traceCycleData.task) {
        this.taskId = this.traceCycleData.task;
      }
        return;
      }
      const [error, data] = await TelemetryService.getTraceCycleRecordData(
        query
      );
      if (error) {
        console.log('Error in getting trace record');
        return;
      }
      this.traceCycleData = data.cycle;
      this.isGoodCycle = data?.cycle?.is_good_cycle;
      if (data?.cycle?.history_id) {
        this.historyId = data.cycle.history_id;
      }
      if (data?.task_record?.Task) {
        this.taskId = data?.task_record?.Task;
      }
    },

    updateMissedSteps() {
      if (!this.video || this.isGoodCycle) return;
      this.missedSteps = this.video.missedStepsName;
    },

    async updatePredictions() {
      if (!this.video) return;
      const { per_frame_prediction_file_url } = this.video;

      try {
        const { data } = await axios.get(per_frame_prediction_file_url);
        const { preds, propagated_steps } = data;
        if (!preds || Object.keys(preds).length === 0) {
          console.error('[preds] downloaded but no usable preds', typeof data);
          this.isPredictionFileNotExist = true;
          this.filePreds = null;
          return;
        }
        const firstFrame = preds[Object.keys(preds)[0]];
        this.$emit('showVisualization', !!(firstFrame && 'bboxes' in firstFrame));
        this.$emit(
          'showClientVisualization',
          !!(firstFrame && 'next_step' in firstFrame)
        );
        this.setStepStartPoint(preds);
        this.setMissedStepStartPoint(data);
        this.propagatedSteps = propagated_steps || [];
        this.filePreds = preds;
        this.precomputeAllSubstepMetrics();
      } catch (error) {
        console.error('[preds] fetch/parse failed', error?.response?.status, error?.message);
        this.isPredictionFileNotExist = true;
        this.filePreds = null;
        this.detectedSteps = {};
        this.missedStepsStartPoints = {};
      }
    },

    handleSeeking(e) {
      const currentTime = this.$refs?.video?.currentTime.toFixed(5);
      this.currentVideoTime = currentTime;
      if (this.isPredictionFileNotExist) return;
      this.setStepsStartTime();
      this.cycleTime = 0;
      const currentFrame = Math.floor(e.target.currentTime * this.video.fps);
      for (let i = 0; i <= currentFrame; i++) {
        this.updateSubstepTimes(i);
      }
      this.$refs.boundingBoxes?.drawBoundingBoxes(currentFrame);
    },

    setStepStartPoint(preds) {
      var tempStepStartTime = {};
      Object.entries(preds).forEach(([frame, value]) => {
        value.pred.forEach((key) => {
          if (!(key in tempStepStartTime)) tempStepStartTime[key] = frame;
        });
      });
      this.stepsStartPoints = tempStepStartTime;
    },

    // Builds a map of substep index -> frame it was first flagged as missed.
    // Primary source is the preds.json top-level parallel arrays
    // `frames_to_show_missed_steps_on` / `lists_of_misssed_steps` (note the
    // source's `misssed` typo). Falls back to scanning each frame's
    // `missed_steps` array if those aren't present.
    setMissedStepStartPoint(data) {
      const temp = {};
      const frames = Array.isArray(data?.frames_to_show_missed_steps_on)
        ? data.frames_to_show_missed_steps_on
        : [];
      const lists = Array.isArray(data?.lists_of_misssed_steps)
        ? data.lists_of_misssed_steps
        : [];
      if (frames.length && lists.length) {
        frames.forEach((frame, i) => {
          // Each entry may be an array of step indices (array-of-arrays shape)
          // or a single step index (flat parallel-array shape). Normalize both,
          // and never assume `.forEach` exists on a non-array.
          const entry = lists[i];
          const stepIndices =
            entry == null ? [] : Array.isArray(entry) ? entry : [entry];
          stepIndices.forEach((stepIndex) => {
            if (!(stepIndex in temp)) temp[stepIndex] = frame; // first occurrence wins
          });
        });
      } else {
        // Fallback: derive from per-frame missed_steps arrays.
        Object.entries(data?.preds || {}).forEach(([frame, value]) => {
          const ms = Array.isArray(value?.missed_steps)
            ? value.missed_steps
            : [];
          ms.forEach((stepIndex) => {
            if (!(stepIndex in temp)) temp[stepIndex] = frame;
          });
        });
      }
      this.missedStepsStartPoints = temp;
    },

    handleClickSubStep(stepIndex) {
      if (!(stepIndex in this.stepsStartPoints)) return;
      const frame = this.stepsStartPoints[stepIndex];
      this.$refs.video.currentTime = frame / this.video.fps;
    },

    handleClickMissedStep(item) {
      if (item?.time == null) return;
      this.skipTo(item.time);
    },

    precomputeAllSubstepMetrics() {
      if (!this.filePreds) return;
      if (localStorage.getItem('organization') !== 'bmwgermanye5cmzyxzt6bi9rso6') return;

      const totalFrames = Object.keys(this.filePreds).length;
      const lookup = {};
      const runningState = {};

      for (let f = 0; f < totalFrames; f++) {
        const frameData = this.filePreds[f];
        if (!frameData) {
          lookup[f] = f > 0 ? lookup[f - 1] : {};
          continue;
        }

        const { pred } = frameData;
        const fps = frameData.fps || 30;

        pred.forEach((stepIndex) => {
          if (stepIndex === 0 || stepIndex === this.taskStepsCount) return;
          if (this.missedStepsFromTelemetry?.includes(stepIndex)) return;

          if (!runningState[stepIndex]) {
            runningState[stepIndex] = {
              firstFrame: f,
              lastFrame: f,
              detectedFrameCount: 0,
              fps,
            };
          }
          runningState[stepIndex].lastFrame = f;
          runningState[stepIndex].detectedFrameCount++;
        });

        const snapshot = {};
        Object.keys(runningState).forEach((stepIndex) => {
          const s = runningState[stepIndex];
          const frameDuration = 1 / s.fps;
          const vaTime = s.detectedFrameCount * frameDuration;
          const totalTime = (s.lastFrame - s.firstFrame + 1) * frameDuration;
          const nvaTime = Math.max(0, totalTime - vaTime);
          snapshot[stepIndex] = { vaTime, nvaTime, totalTime };
        });

        lookup[f] = snapshot;
      }

      this.precomputedMetrics = lookup;
    },

    resetState() {
      this.frame = 0;
      this.currentStepList = [];
      this.currentCycle = 1;
      this.detectedSteps = {};
      this.isPredictionFileNotExist = false;
      this.stepsTime = {};
      this.substepMetrics = {};
      this.precomputedMetrics = {};
      this.missedStepsStartPoints = {};
      cancelAnimationFrame(this.animationFrameId);
    },

    skipTo(timeInSec) {
      this.$refs.video.currentTime = timeInSec;
    },
  },
};
</script>

<style scoped>
.video-options {
  position: absolute;
  right: 5px;
  top: 5px;
  font-size: large;
  z-index: 1;
  padding: 7px;
  transition: all;
}

.video-options:hover {
  color: white;
  background: #a0a0a0;
  border-radius: 50%;
}

/* @-o-keyframes fadeIt {
  0% {
    background-color: #ffffff;
  }
  50% {
    background-color: #ad301b;
  }
  100% {
    background-color: #ffffff;
  }
}
@keyframes fadeIt {
  0% {
    background-color: #ffffff;
  }
  50% {
    background-color: #ad301b;
  }
  100% {
    background-color: #ffffff;
  }
}

.background-animation {
  -o-animation: fadeIt 5s ease-in-out;
  animation: fadeIt 5s ease-in-out;
} */
</style>
